import React from 'react';
import { generateConsentPDF } from '../../common/utils/pdfGenerator';
import { Download, CheckCircle, Shield, Heart, Pen } from 'lucide-react';

const PublicSignedView = ({ client, studio, onBack }) => {

    // Parse JSON safely
    const parse = (val) => {
        if (Array.isArray(val)) return val;
        try {
            return JSON.parse(val || '[]');
        } catch (e) {
            return typeof val === 'string' && val ? [val] : [];
        }
    };

    const agreements = parse(client.agreements);
    const health = parse(client.health_conditions);
    const studioName = studio?.studio_name || 'BookInk Studio';

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
        } catch { return dateStr; }
    };

    // Download Handler
    const handleDownload = () => {
        const doc = generateConsentPDF(client);
        doc.save(`Consent_Form_${client.first_name}_${client.last_name}.pdf`);
    };

    return (
        <div className="flex flex-col min-h-screen font-sans" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' }}>
            {/* Floating Header */}
            <div style={{
                margin: '16px 16px 0',
                borderRadius: '16px',
                background: 'rgba(255,255,255,0.06)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                padding: '16px 20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    {studio?.logo ? (
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}>
                            <img src={studio.logo} alt={studioName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                    ) : (
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 900,
                            color: '#fff',
                            fontSize: '18px',
                            boxShadow: '0 4px 12px rgba(99,102,241,0.4)'
                        }}>
                            {studioName.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div>
                        <div style={{ fontWeight: 800, color: '#fff', fontSize: '16px', letterSpacing: '-0.02em' }}>{studioName}</div>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Signed Consent Record</div>
                    </div>
                </div>
                <button
                    onClick={handleDownload}
                    style={{
                        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                        color: '#fff',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 14px rgba(99,102,241,0.4)',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                    }}
                    onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                    onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
                >
                    <Download size={16} />
                    Download PDF
                </button>
            </div>

            {/* Content Card */}
            <div style={{ flex: 1, padding: '16px', display: 'flex', justifyContent: 'center' }}>
                <div style={{ width: '100%', maxWidth: '720px' }}>

                    {/* Status Banner */}
                    <div style={{
                        background: 'linear-gradient(135deg, #059669, #10b981)',
                        borderRadius: '16px',
                        padding: '20px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        marginBottom: '16px',
                        boxShadow: '0 4px 20px rgba(16,185,129,0.3)'
                    }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <CheckCircle size={22} color="#fff" />
                        </div>
                        <div>
                            <div style={{ fontWeight: 800, color: '#fff', fontSize: '16px' }}>Signed & Verified</div>
                            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', fontWeight: 500 }}>
                                {formatDate(client.signed_at || client.created_at)}
                            </div>
                        </div>
                    </div>

                    {/* Client Details Card */}
                    <div style={{
                        background: 'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        marginBottom: '16px'
                    }}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                <div style={{
                                    width: '28px', height: '28px', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Shield size={14} color="#fff" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#fff' }}>Client Details</h3>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                {[
                                    { label: 'Full Name', value: `${client.first_name} ${client.middle_name ? client.middle_name + ' ' : ''}${client.last_name}` },
                                    { label: 'Email', value: client.email },
                                    { label: 'Date of Birth', value: formatDate(client.dob) },
                                    { label: 'Phone', value: client.phone || '-' },
                                    { label: 'Address', value: client.address || '-' },
                                    { label: 'Postcode', value: client.postcode || '-' },
                                    { label: 'Artist', value: client.staff_name || '-' },
                                    { label: 'Appointment Date', value: formatDate(client.appt_date) },
                                ].map((item, i) => (
                                    <div key={i}>
                                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{item.label}</div>
                                        <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff', wordBreak: 'break-word' }}>{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Agreements Card */}
                    <div style={{
                        background: 'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        marginBottom: '16px'
                    }}>
                        <div style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                <div style={{
                                    width: '28px', height: '28px', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #059669, #10b981)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <CheckCircle size={14} color="#fff" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#fff' }}>Agreements Accepted</h3>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {agreements.length > 0 ? agreements.map((ag, i) => {
                                    const text = typeof ag === 'string' ? ag : (ag.content || ag.title || 'Agreement');
                                    return (
                                        <div key={i} style={{
                                            display: 'flex',
                                            gap: '12px',
                                            padding: '12px 16px',
                                            background: 'rgba(16,185,129,0.08)',
                                            borderRadius: '12px',
                                            border: '1px solid rgba(16,185,129,0.15)'
                                        }}>
                                            <div style={{ color: '#10b981', flexShrink: 0, marginTop: '2px' }}>
                                                <CheckCircle size={16} />
                                            </div>
                                            <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.6' }}>{text}</div>
                                        </div>
                                    );
                                }) : <div style={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', fontSize: '13px' }}>No agreements recorded.</div>}
                            </div>
                        </div>
                    </div>

                    {/* Health Declarations Card */}
                    <div style={{
                        background: 'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        marginBottom: '16px'
                    }}>
                        <div style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                <div style={{
                                    width: '28px', height: '28px', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #ef4444, #f97316)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Heart size={14} color="#fff" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#fff' }}>Health Declarations</h3>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {health.length > 0 ? health.map((h, i) => {
                                    const text = typeof h === 'string' ? h : (h.content || h.title || 'Condition');
                                    return (
                                        <span key={i} style={{
                                            background: 'rgba(239,68,68,0.12)',
                                            color: '#fca5a5',
                                            padding: '6px 14px',
                                            borderRadius: '20px',
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            border: '1px solid rgba(239,68,68,0.2)'
                                        }}>
                                            {text}
                                        </span>
                                    );
                                }) : <div style={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', fontSize: '13px' }}>No conditions declared — all clear ✓</div>}
                            </div>
                            {client.health_details && (
                                <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(239,68,68,0.06)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.1)' }}>
                                    <div style={{ fontSize: '11px', color: '#fca5a5', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>Additional Details</div>
                                    <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', lineHeight: '1.5' }}>{client.health_details}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Signature Card */}
                    <div style={{
                        background: 'rgba(255,255,255,0.06)',
                        backdropFilter: 'blur(16px)',
                        WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        overflow: 'hidden',
                        marginBottom: '24px'
                    }}>
                        <div style={{ padding: '20px 24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                <div style={{
                                    width: '28px', height: '28px', borderRadius: '8px',
                                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                    <Pen size={14} color="#fff" />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#fff' }}>Digital Signature</h3>
                            </div>
                            <div style={{
                                border: '2px dashed rgba(255,255,255,0.12)',
                                borderRadius: '12px',
                                padding: '20px',
                                background: 'rgba(255,255,255,0.03)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '120px'
                            }}>
                                {client.signature ? (
                                    <img src={client.signature} alt="Signature" style={{ maxHeight: '120px', filter: 'invert(1) brightness(2)', opacity: 0.9 }} />
                                ) : (
                                    <div style={{ color: 'rgba(255,255,255,0.3)', fontStyle: 'italic', fontSize: '13px' }}>No signature image available</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ textAlign: 'center', paddingBottom: '32px' }}>
                        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', fontWeight: 500 }}>
                            © {new Date().getFullYear()} {studioName} • Powered by BookINK
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PublicSignedView;
